import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { hideBackButton, setBackButton } from '../../utils/telegram';
import KnowledgeMedia from '../../components/knowledge/KnowledgeMedia';
import { descendantCategoryIds, flattenCategoryTree } from '../../utils/knowledgeTree';

function byOrder(a, b) {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name || a.title || '').localeCompare(String(b.name || b.title || ''), 'ru');
}

export default function KnowledgeBase() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/app/knowledge');
      const next = flattenCategoryTree(
        [...(response.data.categories || [])]
          .map(category => ({ ...category, articles: [...(category.articles || [])].sort(byOrder) }))
      );
      setCategories(next);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить базу знаний');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const goBack = () => navigate('/app');
    setBackButton(goBack);
    return () => hideBackButton(goBack);
  }, [navigate]);

  const articles = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru');
    const selectedIds = selectedCategory === 'all' ? null : descendantCategoryIds(categories, selectedCategory);
    return categories.flatMap(category => (
      category.articles || []
    ).map(article => ({
      ...article,
      category_name: article.category_name || category.name,
      category_path_label: article.category_path_label || category.path_label || category.name,
    })))
      .filter(article => !selectedIds || selectedIds.has(String(article.category_id)))
      .filter(article => !needle || `${article.title || ''} ${article.summary || ''} ${article.category_path_label || ''}`.toLocaleLowerCase('ru').includes(needle));
  }, [categories, query, selectedCategory]);

  return (
    <div className="app-page knowledge-app-page">
      <header className="app-header knowledge-app-header">
        <button type="button" className="btn-back" onClick={() => navigate('/app')} aria-label="Вернуться на главную">←</button>
        <div>
          <div className="home-eyebrow">Инструкции</div>
          <h1>База знаний</h1>
        </div>
      </header>

      <main className="knowledge-app-content">
        <label className="knowledge-app-search">
          <span aria-hidden="true">⌕</span>
          <span className="sr-only">Поиск по базе знаний</span>
          <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Принтер, VPN, пароль..." autoComplete="off" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Очистить поиск">×</button>}
        </label>

        {!loading && !error && categories.length > 0 && (
          <div className="knowledge-category-chips" role="group" aria-label="Фильтр по категории">
            <button type="button" className={selectedCategory === 'all' ? 'active' : ''} onClick={() => setSelectedCategory('all')} aria-pressed={selectedCategory === 'all'}>
              Все
            </button>
            {categories.map(category => (
              <button
                type="button"
                key={category.id}
                className={`${String(selectedCategory) === String(category.id) ? 'active' : ''} ${category.tree_depth ? 'subcategory' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
                aria-pressed={String(selectedCategory) === String(category.id)}
              >
                {category.tree_depth ? '↳ ' : ''}{category.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="knowledge-app-state"><span className="spinner" /><p>Загружаем инструкции...</p></div>
        ) : error ? (
          <div className="knowledge-app-state error">
            <div aria-hidden="true">⚠️</div>
            <h2>Не удалось открыть базу знаний</h2>
            <p>{error}</p>
            <button type="button" className="btn-primary" onClick={load}>Повторить</button>
          </div>
        ) : articles.length === 0 ? (
          <div className="knowledge-app-state">
            <div aria-hidden="true">📚</div>
            <h2>{query ? 'Ничего не найдено' : 'Материалов пока нет'}</h2>
            <p>{query ? 'Попробуйте другой запрос или выберите все категории.' : 'Доступные вам инструкции появятся здесь.'}</p>
          </div>
        ) : (
          <div className="knowledge-app-article-list">
            <div className="knowledge-app-results">{articles.length} {articleCountLabel(articles.length)}</div>
            {articles.map(article => (
              <button
                type="button"
                key={article.id}
                className={`knowledge-app-article-card ${article.cover ? 'has-cover' : ''}`}
                onClick={() => navigate(`/app/knowledge/${article.id}`)}
              >
                <span className="knowledge-app-article-icon" aria-hidden={!article.cover}>
                  {article.cover ? <KnowledgeMedia attachment={article.cover} variant="cover" /> : '📄'}
                </span>
                <span className="knowledge-app-article-copy">
                  <small>{article.category_path_label || article.category_name}</small>
                  <strong>{article.title}</strong>
                  {article.summary && <span>{article.summary}</span>}
                </span>
                <span className="knowledge-app-article-arrow" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function articleCountLabel(count) {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return 'статей';
  if (last === 1) return 'статья';
  if (last >= 2 && last <= 4) return 'статьи';
  return 'статей';
}
